/**
 * Utilidades para manejo de estaciones en filtros
 */

/**
 * Concatena IDs de estaciones con "-"
 * @param {Array|number|string} stationIds - IDs de estaciones (array o valor único)
 * @returns {string} IDs concatenados con "-" (ej: "1-5-10")
 * 
 * @example
 * concatenateStationIds([1, 5, 10]) // "1-5-10"
 * concatenateStationIds(5) // "5"
 * concatenateStationIds("") // ""
 */
export const concatenateStationIds = (stationIds) => {
    if (!stationIds) return '';
    
    if (Array.isArray(stationIds)) {
        return stationIds.filter(id => id).join('-');
    }
    
    return String(stationIds);
};

/**
 * Obtiene nombres de estaciones seleccionadas
 * @param {Array|number|string} stationIds - IDs de estaciones
 * @param {Array} estaciones - Array de estaciones disponibles
 * @returns {Array} Array de nombres de estaciones
 * 
 * @example
 * getStationNames([1, 5], estaciones) // ["Estación 1", "Estación 5"]
 */
export const getStationNames = (stationIds, estaciones = []) => {
    if (!stationIds || !Array.isArray(estaciones)) return [];
    
    const ids = Array.isArray(stationIds) ? stationIds : [stationIds];
    
    return ids
        .map((id) => {
            const station = estaciones.find((s) => s.id === id || s.hash === id);
            return station ? station.name || station.nombre : null;
        })
        .filter(Boolean);
};

/**
 * Obtiene nombre formateado para mostrar en chips/labels
 * @param {Array|number|string} stationIds - IDs de estaciones
 * @param {Array} estaciones - Array de estaciones disponibles
 * @returns {string} Nombres concatenados o resumen
 * 
 * @example
 * getStationDisplayName([1, 5], estaciones) // "Estación 1, Estación 5"
 * getStationDisplayName([1, 2, 3, 4, 5], estaciones) // "5 estaciones seleccionadas"
 */
export const getStationDisplayName = (stationIds, estaciones = []) => {
    const names = getStationNames(stationIds, estaciones);
    
    if (names.length === 0) return '';
    if (names.length === 1) return names[0];
    if (names.length <= 3) return names.join(', ');
    
    return `${names.length} estaciones seleccionadas`;
};

/**
 * Parsea selección de estaciones (string o array)
 * @param {string|Array} selection - Selección en formato "1-5-10" o [1, 5, 10]
 * @returns {Array} Array de IDs
 * 
 * @example
 * parseStationSelection("1-5-10") // [1, 5, 10]
 * parseStationSelection([1, 5, 10]) // [1, 5, 10]
 */
export const parseStationSelection = (selection) => {
    if (!selection) return [];
    
    if (Array.isArray(selection)) {
        return selection.map(id => {
            const parsed = parseInt(id, 10);
            return isNaN(parsed) ? id : parsed;
        });
    }
    
    if (typeof selection === 'string') {
        return selection
            .split('-')
            .map(id => {
                const parsed = parseInt(id.trim(), 10);
                return isNaN(parsed) ? id.trim() : parsed;
            })
            .filter(id => id !== '');
    }
    
    return [selection];
};

/**
 * Valida si hay estaciones seleccionadas
 * @param {Array|string|number} stationIds - IDs de estaciones
 * @returns {boolean} True si hay al menos una estación
 */
export const hasStationSelected = (stationIds) => {
    if (!stationIds) return false;
    
    if (Array.isArray(stationIds)) {
        return stationIds.length > 0;
    }
    
    return String(stationIds).trim() !== '';
};

/**
 * Obtiene estaciones seleccionadas como array
 * @param {Array|string|number} stationIds - IDs de estaciones
 * @returns {Array} Array de IDs
 */
export const getStationIdsAsArray = (stationIds) => {
    if (Array.isArray(stationIds)) {
        return stationIds;
    }
    
    return parseStationSelection(stationIds);
};

export default {
    concatenateStationIds,
    getStationNames,
    getStationDisplayName,
    parseStationSelection,
    hasStationSelected,
    getStationIdsAsArray,
};
